import * as XLSX from 'xlsx';
import { CompanyInfo, ServiceTicket, InventoryItem } from '../types';
import {
  calculateTicketTotal,
  calculateTicketPartsCost,
  calculatePaymentBreakdownForPeriod,
  STATUS_LABELS,
  getTicketReportDate,
  getTicketPaymentSplit,
  getTicketPaymentSplitForPeriod,
  formatTicketPaymentMethodDisplay,
  formatPhoneDisplay,
  getStoredExchangeRate,
} from './storage';

const getPeriodCollectionRows = (tickets: ServiceTicket[], periodKey: string) => tickets.flatMap(ticket => {
  if (ticket.isDeleted || ticket.serviceType === 'Sozlesmeli' || ticket.contractedCompanyId) return [];
  const payments = Array.isArray(ticket.payments) ? ticket.payments : [];
  if (payments.length) return payments.filter(p => (periodKey === 'all' || p.date?.startsWith(periodKey)) && Number(p.amount) > 0).map(p => ({
    'Ödeme Tarihi': p.date?.substring(0, 16).replace('T', ' ') || '',
    'Servis Formu': ticket.id,
    'Müşteri': ticket.customerName || '',
    'Cihaz': `${ticket.deviceType || ''} ${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim(),
    'Ödeme Türü': p.method || 'Nakit',
    'Alınan Tutar (TL)': Number(p.amount) || 0,
  }));
  if (periodKey !== 'all' && !ticket.paymentDate?.startsWith(periodKey)) return [];
  const split = periodKey === 'all' ? getTicketPaymentSplit(ticket) : getTicketPaymentSplitForPeriod(ticket, periodKey);
  const legacyMethods: Array<[string, number]> = [
    ['Nakit', split.cash], ['Kredi Kartı', split.card], ['EFT/Havale', split.transfer],
  ];
  return legacyMethods.filter(([, amount]) => amount > 0).map(([method, amount]) => ({
    'Ödeme Tarihi': ticket.paymentDate?.substring(0, 16).replace('T', ' ') || '',
    'Servis Formu': ticket.id,
    'Müşteri': ticket.customerName || '',
    'Cihaz': `${ticket.deviceType || ''} ${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim(),
    'Ödeme Türü': method,
    'Alınan Tutar (TL)': Number(amount) || 0,
  }));
});

export function exportMonthlyReportExcel(
  monthKey: string,
  formattedMonthName: string,
  tickets: ServiceTicket[],
  companyInfo: CompanyInfo
) {
  // Filter tickets for month based on report date
  const eligibleTickets = tickets.filter(t =>
    !t.isDeleted && t.serviceType !== 'Sozlesmeli' && !t.contractedCompanyId
  );
  const monthTickets = eligibleTickets.filter(t => Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(monthKey)));
  const payBreakdown = calculatePaymentBreakdownForPeriod(eligibleTickets, monthKey);

  let totalRevenue = 0;
  let totalVat = 0;
  let totalExVatRevenue = 0;
  let totalPartsCost = 0;
  let totalSubServiceCost = 0;
  let totalLaborRevenue = 0;
  let totalPartsSale = 0;
  let totalCollected = 0;
  let totalRemaining = 0;
  let deliveredCount = 0;

  const ticketRows = monthTickets.map(t => {
    const isServiceMonth = Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(monthKey));
    const { subtotal, vatAmount, partsTotal, grandTotal } = calculateTicketTotal(
      t.parts,
      t.laborCost,
      t.vatRate,
      t.discount,
      t.isVatIncluded, t.roundingAdjustment || 0
    );
    const partsCost = calculateTicketPartsCost(t.parts);
    const subCost = t.subServiceCost || 0;
    const periodPayment = getTicketPaymentSplitForPeriod(t, monthKey);
    const paid = periodPayment.total;
    const recognizedRatio = grandTotal > 0 ? paid / grandTotal : 0;
    const recognizedVat = vatAmount * recognizedRatio;
    const recognizedSubtotal = subtotal * recognizedRatio;
    const recognizedPartsCost = partsCost * recognizedRatio;
    const recognizedSubCost = subCost * recognizedRatio;
    const recognizedTotalCost = recognizedPartsCost + recognizedSubCost;
    const remaining = Math.max(0, grandTotal - getTicketPaymentSplit(t).total);
    const grossProfit = paid - recognizedTotalCost;
    const netProfitExVat = recognizedSubtotal - recognizedTotalCost;
    const methodLabels = [
      periodPayment.cash > 0 ? 'Nakit' : '',
      periodPayment.card > 0 ? 'Kredi Kartı' : '',
      periodPayment.transfer > 0 ? 'EFT/Havale' : '',
    ].filter(Boolean);

    totalRevenue += paid;
    totalVat += recognizedVat;
    totalExVatRevenue += recognizedSubtotal;
    totalPartsCost += recognizedPartsCost;
    totalSubServiceCost += recognizedSubCost;
    totalLaborRevenue += t.laborCost * recognizedRatio;
    totalPartsSale += partsTotal * recognizedRatio;
    totalCollected += paid;
    totalRemaining += remaining;

    if (isServiceMonth && t.status === 'TeslimEdildi') {
      deliveredCount++;
    }

    return {
      'Servis No': t.id,
      'Kayıt Tarihi': t.createdAt ? t.createdAt.substring(0, 10) : '',
      'Teslim / İşlem Tarihi': t.deliveryDate ? t.deliveryDate.substring(0, 10) : '',
      'Müşteri Adı': t.customerName || '',
      'Telefon': t.customerPhone || '',
      'Cihaz Türü': t.deviceType || '',
      'Marka': t.deviceBrand || '',
      'Model': t.deviceModel || '',
      'Seri / IMEI No': t.serialNumber || '',
      'Garanti Durumu': t.isUnderWarranty ? 'Garantili' : 'Garantisiz',
      'Servis Durumu': STATUS_LABELS[t.status] || t.status,
      'Alt Servis Firma': t.subServiceName || '',
      'Alt Servis Maliyeti (TL)': recognizedSubCost,
      'Alt Servis Ödeme Durumu': t.subServicePaymentStatus === 'Odendi' ? 'Ödendi' : subCost > 0 ? 'Ödenmedi (Borç)' : '',
      'Alt Servis Takip No': t.subServiceTrackingNo || '',
      'Şikayet / Arıza': t.issueDescription || '',
      'Parça Maliyeti (TL)': recognizedPartsCost,
      'Parça Satış (TL)': partsTotal * recognizedRatio,
      'İşçilik (TL)': t.laborCost * recognizedRatio,
      'İndirim (TL)': t.discount,
      'KDV Oranı (%)': `%20`,
      'KDV Tutarı (TL)': recognizedVat,
      'KDV Hariç Matrah (TL)': recognizedSubtotal,
      'Toplam Tutar (TL)': grandTotal,
      'Ödeme Durumu':
        t.paymentStatus === 'Odendi'
          ? 'Ödendi'
          : t.paymentStatus === 'KismiOdendi'
          ? 'Kısmi Ödendi'
          : 'Ödenmedi',
      'Ödeme Yöntemi': methodLabels.length > 0 ? methodLabels.join(' + ') : '-',
      'Alınan Ödeme (Tahsilat TL)': paid,
      'Kalan Alacak (TL)': remaining,
      'KDV Düşülmüş Net Kâr (TL)': netProfitExVat,
      'Brüt Kâr (TL)': grossProfit,
    };
  });

  totalRevenue = payBreakdown.totalCollected;
  totalVat = 0;
  totalExVatRevenue = 0;
  totalLaborRevenue = 0;
  eligibleTickets.forEach(t => {
    const received = getTicketPaymentSplitForPeriod(t, monthKey).total;
    if (received <= 0) return;
    const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
    const ratio = grandTotal > 0 ? received / grandTotal : 0;
    totalVat += vatAmount * ratio;
    totalExVatRevenue += subtotal * ratio;
    totalLaborRevenue += (t.laborCost || 0) * ratio;
  });

  const totalAllCost = totalPartsCost + totalSubServiceCost;

  // Summary sheet rows
  const summaryRows = [
    { Metrik: 'Firma Adı', Değer: companyInfo.name || 'Teknik Servis' },
    { Metrik: 'Rapor Türü', Değer: 'Aylık Servis & Finansal Rapor' },
    { Metrik: 'Rapor Dönemi', Değer: formattedMonthName },
    { Metrik: 'Raporlama Tarihi', Değer: new Date().toLocaleDateString('tr-TR') },
    { Metrik: 'Toplam Servis Kaydı', Değer: monthTickets.length },
    { Metrik: 'Teslim Edilen Cihaz Sayısı', Değer: deliveredCount },
    { Metrik: 'Seçilen Ayda Alınan Tahsilat (KDV Dahil TL)', Değer: payBreakdown.totalCollected },
    { Metrik: 'Toplam Hesaplanan KDV Tutarı (TL)', Değer: totalVat },
    { Metrik: 'Toplam KDV Hariç Matrah / Ciro (TL)', Değer: totalExVatRevenue },
    { Metrik: '--- KASA & TAHSİLAT DAĞILIMI ---', Değer: '----------------' },
    { Metrik: '--- ÖDEME TÜRÜ DAĞILIMI ---', Değer: '----------------' },
    { Metrik: '  * Nakit Tahsilat (TL)', Değer: `${payBreakdown.cash.amount} TL (${payBreakdown.cash.count} Adet)` },
    { Metrik: '  * Kredi Kartı / POS Tahsilat (TL)', Değer: `${payBreakdown.card.amount} TL (${payBreakdown.card.count} Adet)` },
    { Metrik: '  * Hesaba Havale / EFT Tahsilat (TL)', Değer: `${payBreakdown.transfer.amount} TL (${payBreakdown.transfer.count} Adet)` },
    { Metrik: '---------------------------------', Değer: '----------------' },
    { Metrik: 'Seçilen Ayın Servis Formlarından Alınacak (Kalan Bakiye TL)', Değer: totalRemaining },
    { Metrik: 'Toplam Yedek Parça Maliyeti (TL)', Değer: totalPartsCost },
    { Metrik: 'Toplam Alt Servis Gideri (TL)', Değer: totalSubServiceCost },
    { Metrik: 'Tahsilata Isabet Eden İşçilik (TL)', Değer: totalLaborRevenue },
    { Metrik: 'Brüt Kâr (Ciro - Parça & Alt Servis Maliyeti TL)', Değer: totalRevenue - totalAllCost },
    { Metrik: 'KDV Düşülmüş Net Kâr (Matrah - Parça & Alt Servis TL)', Değer: totalExVatRevenue - totalAllCost },
  ];

  const wb = XLSX.utils.book_new();

  const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
  const wsDetails = XLSX.utils.json_to_sheet(
    ticketRows.length > 0
      ? ticketRows
      : [
          {
            'Servis No': 'Kayıt Yok',
            'Kayıt Tarihi': '',
            'Müşteri Adı': '',
            'Telefon': '',
            'Cihaz Türü': '',
            'Marka': '',
            'Model': '',
            'Seri / IMEI No': '',
            'Garanti Durumu': '',
            'Servis Durumu': '',
            'Şikayet / Arıza': '',
            'Parça Maliyeti (TL)': 0,
            'Parça Satış (TL)': 0,
            'İşçilik (TL)': 0,
            'İndirim (TL)': 0,
            'KDV Oranı (%)': '',
            'KDV Tutarı (TL)': 0,
            'KDV Hariç Matrah (TL)': 0,
            'Toplam Tutar (TL)': 0,
            'Ödeme Durumu': '',
            'Alınan Ödeme (Tahsilat TL)': 0,
            'Kalan Alacak (TL)': 0,
            'KDV Düşülmüş Net Kâr (TL)': 0,
            'Brüt Kâr (TL)': 0,
          },
        ]
  );
  const collectionRows = getPeriodCollectionRows(eligibleTickets, monthKey);
  const wsCollections = XLSX.utils.json_to_sheet(collectionRows.length ? collectionRows : [{ 'Ödeme Tarihi': '', 'Servis Formu': '', 'Müşteri': 'Bu ay tahsilat kaydı yok', 'Cihaz': '', 'Ödeme Türü': '', 'Alınan Tutar (TL)': 0 }]);

  // Auto-fit column width helper
  const autoFitColumns = (ws: XLSX.WorkSheet, rows: any[]) => {
    if (!rows || rows.length === 0) return;
    const colWidths = Object.keys(rows[0]).map(key => {
      let maxLen = key.toString().length;
      rows.forEach(r => {
        const val = r[key];
        if (val !== null && val !== undefined) {
          maxLen = Math.max(maxLen, val.toString().length);
        }
      });
      return { wch: Math.min(Math.max(maxLen + 3, 12), 45) };
    });
    ws['!cols'] = colWidths;
  };

  autoFitColumns(wsSummary, summaryRows);
  autoFitColumns(wsDetails, ticketRows);

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Genel Özet');
  XLSX.utils.book_append_sheet(wb, wsDetails, 'Servis Kayıtları');
  XLSX.utils.book_append_sheet(wb, wsCollections, 'Tahsilat Hareketleri');

  XLSX.writeFile(wb, `Aylik_Servis_Raporu_${monthKey}.xlsx`);
}

/**
 * Daily Report Excel Export
 */
export function exportDailyReportExcel(
  dateKey: string,
  formattedDateName: string,
  tickets: ServiceTicket[],
  companyInfo: CompanyInfo
) {
  const dayTickets = tickets.filter(
    t =>
      !t.isDeleted &&
      t.serviceType !== 'Sozlesmeli' &&
      !t.contractedCompanyId &&
      Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(dateKey))
  );
  const payBreakdown = calculatePaymentBreakdownForPeriod(
    tickets.filter(t => !t.isDeleted && t.serviceType !== 'Sozlesmeli' && !t.contractedCompanyId),
    dateKey
  );

  let totalRevenue = 0;
  let totalVat = 0;
  let totalExVatRevenue = 0;
  let totalPartsCost = 0;
  let totalSubServiceCost = 0;
  let totalLaborRevenue = 0;
  let totalPartsSale = 0;
  let totalCollected = 0;
  let totalRemaining = 0;
  let deliveredCount = 0;

  const ticketRows = dayTickets.map(t => {
    const { subtotal, vatAmount, partsTotal, grandTotal } = calculateTicketTotal(
      t.parts,
      t.laborCost,
      t.vatRate,
      t.discount,
      t.isVatIncluded, t.roundingAdjustment || 0
    );
    const partsCost = calculateTicketPartsCost(t.parts);
    const subCost = t.subServiceCost || 0;
    const totalTicketCost = partsCost + subCost;
    const paid = getTicketPaymentSplitForPeriod(t, dateKey).total;
    const allTimePaid = getTicketPaymentSplit(t).total;
    const remaining = Math.max(0, grandTotal - allTimePaid);
    const recognizedRatio = grandTotal > 0 ? paid / grandTotal : 0;
    const recognizedRevenue = paid;
    const recognizedVat = vatAmount * recognizedRatio;
    const recognizedSubtotal = subtotal * recognizedRatio;
    const recognizedCost = totalTicketCost;
    const grossProfit = recognizedRevenue - recognizedCost;
    const netProfitExVat = recognizedSubtotal - recognizedCost;

    totalRevenue += recognizedRevenue;
    totalVat += recognizedVat;
    totalExVatRevenue += recognizedSubtotal;
    totalPartsCost += partsCost * recognizedRatio;
    totalSubServiceCost += subCost * recognizedRatio;
    totalLaborRevenue += t.laborCost * recognizedRatio;
    totalPartsSale += partsTotal * recognizedRatio;
    totalCollected += paid;
    totalRemaining += remaining;

    if (t.status === 'TeslimEdildi') {
      deliveredCount++;
    }

    return {
      'Servis No': t.id,
      'Kayıt Tarihi': t.createdAt ? t.createdAt.substring(0, 10) : '',
      'Teslim / İşlem Tarihi': t.deliveryDate ? t.deliveryDate.substring(0, 10) : '',
      'Müşteri Adı': t.customerName || '',
      'Telefon': t.customerPhone || '',
      'Cihaz Türü': t.deviceType || '',
      'Marka': t.deviceBrand || '',
      'Model': t.deviceModel || '',
      'Seri / IMEI No': t.serialNumber || '',
      'Garanti Durumu': t.isUnderWarranty ? 'Garantili' : 'Garantisiz',
      'Servis Durumu': STATUS_LABELS[t.status] || t.status,
      'Alt Servis Firma': t.subServiceName || '',
      'Alt Servis Maliyeti (TL)': subCost,
      'Alt Servis Ödeme Durumu': t.subServicePaymentStatus === 'Odendi' ? 'Ödendi' : subCost > 0 ? 'Ödenmedi (Borç)' : '',
      'Alt Servis Takip No': t.subServiceTrackingNo || '',
      'Şikayet / Arıza': t.issueDescription || '',
      'Parça Maliyeti (TL)': partsCost,
      'Parça Satış (TL)': partsTotal,
      'İşçilik (TL)': t.laborCost,
      'İndirim (TL)': t.discount,
      'KDV Oranı (%)': `%20`,
      'KDV Tutarı (TL)': recognizedVat,
      'KDV Hariç Matrah (TL)': recognizedSubtotal,
      'Toplam Tutar (TL)': recognizedRevenue,
      'Ödeme Durumu':
        t.paymentStatus === 'Odendi'
          ? 'Ödendi'
          : t.paymentStatus === 'KismiOdendi'
          ? 'Kısmi Ödendi'
          : 'Ödenmedi',
      'Alınan Ödeme (Tahsilat TL)': paid,
      'Kalan Alacak (TL)': remaining,
      'KDV Düşülmüş Net Kâr (TL)': netProfitExVat,
      'Brüt Kâr (TL)': grossProfit,
    };
  });

  totalRevenue = payBreakdown.totalCollected;
  totalVat = 0;
  totalExVatRevenue = 0;
  totalLaborRevenue = 0;
  const eligibleDailyTickets = tickets.filter(t => !t.isDeleted && t.serviceType !== 'Sozlesmeli' && !t.contractedCompanyId);
  eligibleDailyTickets.forEach(t => {
    const received = getTicketPaymentSplitForPeriod(t, dateKey).total;
    if (received <= 0) return;
    const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
    const ratio = grandTotal > 0 ? received / grandTotal : 0;
    totalVat += vatAmount * ratio;
    totalExVatRevenue += subtotal * ratio;
    totalLaborRevenue += (t.laborCost || 0) * ratio;
  });

  const totalDailyAllCost = totalPartsCost + totalSubServiceCost;

  // Summary sheet rows
  const summaryRows = [
    { Metrik: 'Firma Adı', Değer: companyInfo.name || 'Teknik Servis' },
    { Metrik: 'Rapor Türü', Değer: 'Günlük Servis, Teslimat & Kasa Raporu' },
    { Metrik: 'Rapor Tarihi', Değer: formattedDateName },
    { Metrik: 'Rapor Oluşturma Zamanı', Değer: new Date().toLocaleString('tr-TR') },
    { Metrik: 'Toplam Günlük Kayıt', Değer: dayTickets.length },
    { Metrik: 'Teslim Edilen Cihaz Sayısı', Değer: deliveredCount },
    { Metrik: 'Seçilen Günde Alınan Tahsilat (KDV Dahil TL)', Değer: payBreakdown.totalCollected },
    { Metrik: 'Toplam Hesaplanan KDV Tutarı (TL)', Değer: totalVat },
    { Metrik: 'Toplam KDV Hariç Matrah (TL)', Değer: totalExVatRevenue },
    { Metrik: '--- GÜNLÜK ÖDEME TÜRÜ DAĞILIMI ---', Değer: '----------------' },
    { Metrik: 'Alınacak Ücretler (Kalan Toplam Alacak) (TL)', Değer: totalRemaining },
    { Metrik: 'Toplam Yedek Parça Maliyeti (TL)', Değer: totalPartsCost },
    { Metrik: 'Toplam Alt Servis Gideri (TL)', Değer: totalSubServiceCost },
    { Metrik: 'Toplam İşçilik Geliri (TL)', Değer: totalLaborRevenue },
    { Metrik: 'Brüt Kâr (Ciro - Parça & Alt Servis Maliyeti TL)', Değer: totalRevenue - totalDailyAllCost },
    { Metrik: 'Günlük KDV Düşülmüş Net Kâr (TL)', Değer: totalExVatRevenue - totalDailyAllCost },
  ];

  const wb = XLSX.utils.book_new();

  const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
  const wsDetails = XLSX.utils.json_to_sheet(
    ticketRows.length > 0
      ? ticketRows
      : [
          {
            'Servis No': 'Kayıt Yok',
            'Kayıt Tarihi': '',
            'Müşteri Adı': '',
            'Telefon': '',
            'Cihaz Türü': '',
            'Marka': '',
            'Model': '',
            'Seri / IMEI No': '',
            'Garanti Durumu': '',
            'Servis Durumu': '',
            'Şikayet / Arıza': '',
            'Parça Maliyeti (TL)': 0,
            'Parça Satış (TL)': 0,
            'İşçilik (TL)': 0,
            'İndirim (TL)': 0,
            'KDV Oranı (%)': '',
            'KDV Tutarı (TL)': 0,
            'KDV Hariç Matrah (TL)': 0,
            'Toplam Tutar (TL)': 0,
            'Ödeme Durumu': '',
            'Alınan Ödeme (Tahsilat TL)': 0,
            'Kalan Alacak (TL)': 0,
            'KDV Düşülmüş Net Kâr (TL)': 0,
            'Brüt Kâr (TL)': 0,
          },
        ]
  );
  const collectionRows = getPeriodCollectionRows(tickets, dateKey);
  const wsCollections = XLSX.utils.json_to_sheet(collectionRows.length ? collectionRows : [{ 'Ödeme Tarihi': '', 'Servis Formu': '', 'Müşteri': 'Bu gün tahsilat kaydı yok', 'Cihaz': '', 'Ödeme Türü': '', 'Alınan Tutar (TL)': 0 }]);

  const autoFitColumns = (ws: XLSX.WorkSheet, rows: any[]) => {
    if (!rows || rows.length === 0) return;
    const colWidths = Object.keys(rows[0]).map(key => {
      let maxLen = key.toString().length;
      rows.forEach(r => {
        const val = r[key];
        if (val !== null && val !== undefined) {
          maxLen = Math.max(maxLen, val.toString().length);
        }
      });
      return { wch: Math.min(Math.max(maxLen + 3, 12), 45) };
    });
    ws['!cols'] = colWidths;
  };

  autoFitColumns(wsSummary, summaryRows);
  autoFitColumns(wsDetails, ticketRows);

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Günlük Özet');
  XLSX.utils.book_append_sheet(wb, wsDetails, 'Servis Kayıtları');
  XLSX.utils.book_append_sheet(wb, wsCollections, 'Tahsilat Hareketleri');

  XLSX.writeFile(wb, `Gunluk_Servis_Raporu_${dateKey}.xlsx`);
}

export function exportInventoryExcel(
  items: any[],
  movements: any[],
  companyInfo: CompanyInfo
) {
  const wb = XLSX.utils.book_new();

  // Summary Metrics
  const totalKinds = items.length;
  const totalUnits = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const totalCostVal = items.reduce((s, i) => s + (i.quantity || 0) * (i.costPrice || 0), 0);
  const totalSaleVal = items.reduce((s, i) => s + (i.quantity || 0) * (i.salePrice || 0), 0);
  const criticalCount = items.filter(i => (i.quantity || 0) <= (i.minQuantity || 2) && (i.quantity || 0) > 0).length;
  const outOfStockCount = items.filter(i => (i.quantity || 0) <= 0).length;

  const summaryRows = [
    { 'Metrik': 'Firma Adı', 'Değer': companyInfo.name || 'Teknik Servis' },
    { 'Metrik': 'Rapor Tarihi', 'Değer': new Date().toLocaleDateString('tr-TR') },
    { 'Metrik': 'Toplam Farklı Ürün / Parça Çeşidi', 'Değer': totalKinds },
    { 'Metrik': 'Depodaki Toplam Stok Adedi (Birim)', 'Değer': totalUnits },
    { 'Metrik': 'Toplam Stok Alış / Maliyet Değeri (TL)', 'Değer': totalCostVal },
    { 'Metrik': 'Toplam Stok Satış / Potansiyel Ciro Değeri (TL)', 'Değer': totalSaleVal },
    { 'Metrik': 'Potansiyel Brüt Stok Kârı (TL)', 'Değer': totalSaleVal - totalCostVal },
    { 'Metrik': 'Kritik Seviyedeki Parçalar (Adet)', 'Değer': criticalCount },
    { 'Metrik': 'Tükenen Parçalar (Adet)', 'Değer': outOfStockCount },
  ];

  const itemRows = items.map(item => {
    const qty = item.quantity || 0;
    const cost = item.costPrice || 0;
    const sale = item.salePrice || 0;
    const minQ = item.minQuantity || 2;
    let statusText = 'Yeterli';
    if (qty <= 0) statusText = 'TÜKENDİ';
    else if (qty <= minQ) statusText = 'KRİTİK STOK';

    return {
      'Stok Kodu / Barkod': item.code || '-',
      'Ürün / Parça Adı': item.name,
      'Kategori': item.category || 'Genel',
      'Uyumlu Marka / Model': [item.brand, item.model].filter(Boolean).join(' ') || '-',
      'Mevcut Stok (Adet)': qty,
      'Kritik Eşik': minQ,
      'Durum': statusText,
      'Para Birimi': item.currency === 'USD' ? 'USD ($)' : 'TRY (₺)',
      'Dolar Alış ($)': item.currency === 'USD' && item.costPriceUSD !== undefined ? item.costPriceUSD : '-',
      'Dolar Satış ($)': item.currency === 'USD' && item.salePriceUSD !== undefined ? item.salePriceUSD : '-',
      'Birim Alış Fiyatı (TL)': cost,
      'Birim Satış Fiyatı (TL)': sale,
      'Toplam Maliyet (TL)': qty * cost,
      'Toplam Satış Değeri (TL)': qty * sale,
      'Birim Kâr (TL)': sale - cost,
      'Kâr Oranı (%)': cost > 0 ? `%${Math.round(((sale - cost) / cost) * 100)}` : '%0',
      'Raf / Çekmece Konumu': item.shelfLocation || '-',
      'Tedarikçi Firma': item.supplier || '-',
      'Açıklama / Not': item.notes || '-',
    };
  });

  const movementRows = movements.map(m => {
    const typeLabel = m.type === 'in' ? 'Stok Girişi (Alım)' : m.type === 'out' ? 'Stok Çıkışı (Servis/Satış)' : 'Sayım Düzeltme';
    return {
      'Tarih': new Date(m.date).toLocaleString('tr-TR'),
      'İşlem Türü': typeLabel,
      'Ürün Adı': m.itemName,
      'Stok Kodu': m.itemCode || '-',
      'Değişim Miktarı (Adet)': m.type === 'out' ? -Math.abs(m.quantity) : Math.abs(m.quantity),
      'Önceki Stok': m.previousQuantity,
      'Yeni Stok': m.newQuantity,
      'İlgili Servis No': m.ticketId || '-',
      'Açıklama / Not': m.note || '-',
    };
  });

  const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
  const wsItems = XLSX.utils.json_to_sheet(itemRows);
  const wsMovements = XLSX.utils.json_to_sheet(movementRows.length > 0 ? movementRows : [{ Bilgi: 'Henüz kayıtlı stok hareketi yok' }]);

  const autoFitColumns = (ws: XLSX.WorkSheet, rows: any[]) => {
    if (!rows || rows.length === 0) return;
    const colWidths = Object.keys(rows[0]).map(key => {
      let maxLen = key.toString().length;
      rows.forEach(r => {
        const val = r[key];
        if (val !== null && val !== undefined) {
          maxLen = Math.max(maxLen, val.toString().length);
        }
      });
      return { wch: Math.min(Math.max(maxLen + 3, 12), 45) };
    });
    ws['!cols'] = colWidths;
  };

  autoFitColumns(wsSummary, summaryRows);
  autoFitColumns(wsItems, itemRows);
  if (movementRows.length > 0) autoFitColumns(wsMovements, movementRows);

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Stok Özeti');
  XLSX.utils.book_append_sheet(wb, wsItems, 'Stok ve Parça Listesi');
  XLSX.utils.book_append_sheet(wb, wsMovements, 'Stok Hareketleri');

  const today = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `Stok_Envanter_Raporu_${today}.xlsx`);
}

/**
 * Export Dedicated Stock Deduction & Used Parts Report to Excel (.xlsx)
 */
export function exportStockUsageReportExcel(
  periodKey: string,
  formattedPeriodName: string,
  tickets: ServiceTicket[],
  inventory: InventoryItem[] = [],
  companyInfo: CompanyInfo
) {
  const wb = XLSX.utils.book_new();

  // Filter tickets by period if not 'all'
  const filteredTickets = periodKey === 'all'
    ? tickets
    : tickets.filter(t => {
        return Boolean(t.createdAt && t.createdAt.startsWith(periodKey));
      });

  // Extract all used parts from tickets
  interface DetailedPartRow {
    date: string;
    ticketId: string;
    customerName: string;
    customerPhone: string;
    device: string;
    partName: string;
    partCode: string;
    category: string;
    quantity: number;
    unitCost: number;
    unitPrice: number;
    totalCost: number;
    totalSale: number;
    netProfit: number;
    marginPercent: number;
    currentStock: number | string;
    isStocked: boolean;
    // FX & Currency Tracking
    currency: string;
    isUSD: boolean;
    costPriceUSD?: number;
    salePriceUSD?: number;
    costExchangeRate?: number;
    saleExchangeRate?: number;
    rateDifference?: number;
    fxGainTL?: number;
  }

  const detailedRows: DetailedPartRow[] = [];
  const partAggMap = new Map<
    string,
    {
      partName: string;
      partCode: string;
      category: string;
      totalQty: number;
      totalCost: number;
      totalSale: number;
      timesUsed: number;
      currentStock: number | string;
      isUSD: boolean;
      totalCostUSD: number;
      totalSaleUSD: number;
      totalFxGainTL: number;
      costRateWeightedSum: number;
      costUSDQtySum: number;
      saleRateWeightedSum: number;
      saleUSDQtySum: number;
    }
  >();

  let grandTotalQty = 0;
  let grandTotalCost = 0;
  let grandTotalSale = 0;
  let grandTotalCostUSD = 0;
  let grandTotalSaleUSD = 0;
  let grandTotalFxGainTL = 0;
  let grandCostRateSum = 0;
  let grandCostUSDQty = 0;
  let grandSaleRateSum = 0;
  let grandSaleUSDQty = 0;

  const liveRate = getStoredExchangeRate().rate || 47.95;

  filteredTickets.forEach(t => {
    const tDate = (t.createdAt || '').substring(0, 10);
    const deviceStr = [t.deviceType, t.deviceBrand, t.deviceModel].filter(Boolean).join(' ');

    (t.parts || []).forEach(p => {
      const qty = p.quantity || 1;

      const invItem = p.inventoryItemId
        ? inventory.find(i => i.id === p.inventoryItemId)
        : inventory.find(i => i.name.trim().toLowerCase() === p.name.trim().toLowerCase());

      const isUSD =
        p.currency === 'USD' ||
        invItem?.currency === 'USD' ||
        (p.costPriceUSD !== undefined && p.costPriceUSD > 0) ||
        (invItem?.costPriceUSD !== undefined && (invItem.costPriceUSD || 0) > 0);

      const costUSD =
        p.costPriceUSD !== undefined && p.costPriceUSD > 0
          ? p.costPriceUSD
          : invItem?.costPriceUSD !== undefined && (invItem.costPriceUSD || 0) > 0
          ? invItem.costPriceUSD
          : undefined;

      const saleUSD =
        p.unitPriceUSD !== undefined && p.unitPriceUSD > 0
          ? p.unitPriceUSD
          : invItem?.salePriceUSD !== undefined && (invItem.salePriceUSD || 0) > 0
          ? invItem.salePriceUSD
          : undefined;

      const costExchangeRate =
        p.costExchangeRate ||
        invItem?.costExchangeRate ||
        invItem?.exchangeRate ||
        p.exchangeRate ||
        (isUSD ? liveRate : undefined);

      const saleExchangeRate =
        p.saleExchangeRate ||
        p.exchangeRate ||
        (isUSD ? liveRate : undefined);

      let uCost = p.costPrice !== undefined ? p.costPrice : 0;
      if (isUSD && costUSD && costExchangeRate && uCost <= 0) {
        uCost = Math.round(costUSD * costExchangeRate * 100) / 100;
      } else if (uCost <= 0 && invItem?.costPrice) {
        uCost = invItem.costPrice;
      }

      let uPrice = p.unitPrice || 0;
      if (isUSD && saleUSD && saleExchangeRate && uPrice <= 0) {
        uPrice = Math.round(saleUSD * saleExchangeRate * 100) / 100;
      } else if (uPrice <= 0 && invItem?.salePrice) {
        uPrice = invItem.salePrice;
      }

      const rowCost = qty * uCost;
      const rowSale = qty * uPrice;
      const rowProfit = rowSale - rowCost;
      const margin = rowCost > 0 ? Math.round((rowProfit / rowCost) * 100) : (rowSale > 0 ? 100 : 0);

      const rateDifference =
        isUSD && costExchangeRate && saleExchangeRate
          ? Math.round((saleExchangeRate - costExchangeRate) * 10000) / 10000
          : undefined;

      const fxGainTL =
        isUSD && costUSD && costExchangeRate && saleExchangeRate
          ? Math.round((saleExchangeRate - costExchangeRate) * costUSD * qty * 100) / 100
          : 0;

      const partCode = invItem?.code || invItem?.id || '-';
      const category = invItem?.category || 'Genel';
      const currentStock = invItem ? (invItem.quantity ?? 0) : 'Harici';

      grandTotalQty += qty;
      grandTotalCost += rowCost;
      grandTotalSale += rowSale;

      if (isUSD) {
        if (costUSD) {
          grandTotalCostUSD += costUSD * qty;
          if (costExchangeRate) {
            grandCostRateSum += costExchangeRate * qty;
            grandCostUSDQty += qty;
          }
        }
        if (saleUSD) {
          grandTotalSaleUSD += saleUSD * qty;
          if (saleExchangeRate) {
            grandSaleRateSum += saleExchangeRate * qty;
            grandSaleUSDQty += qty;
          }
        }
        if (fxGainTL) {
          grandTotalFxGainTL += fxGainTL;
        }
      }

      detailedRows.push({
        date: tDate,
        ticketId: t.id,
        customerName: t.customerName || '',
        customerPhone: t.customerPhone ? formatPhoneDisplay(t.customerPhone) : '',
        device: deviceStr,
        partName: p.name,
        partCode,
        category,
        quantity: qty,
        unitCost: uCost,
        unitPrice: uPrice,
        totalCost: rowCost,
        totalSale: rowSale,
        netProfit: rowProfit,
        marginPercent: margin,
        currentStock,
        isStocked: !!invItem,
        currency: isUSD ? 'USD' : 'TRY',
        isUSD,
        costPriceUSD: costUSD,
        salePriceUSD: saleUSD,
        costExchangeRate,
        saleExchangeRate,
        rateDifference,
        fxGainTL,
      });

      // Aggregation
      const aggKey = invItem ? invItem.id : p.name.trim().toLowerCase();
      const existingAgg = partAggMap.get(aggKey) || {
        partName: p.name,
        partCode,
        category,
        totalQty: 0,
        totalCost: 0,
        totalSale: 0,
        timesUsed: 0,
        currentStock,
        isUSD,
        totalCostUSD: 0,
        totalSaleUSD: 0,
        totalFxGainTL: 0,
        costRateWeightedSum: 0,
        costUSDQtySum: 0,
        saleRateWeightedSum: 0,
        saleUSDQtySum: 0,
      };

      existingAgg.totalQty += qty;
      existingAgg.totalCost += rowCost;
      existingAgg.totalSale += rowSale;
      existingAgg.timesUsed += 1;

      if (isUSD) {
        existingAgg.isUSD = true;
        if (costUSD) {
          existingAgg.totalCostUSD += costUSD * qty;
          if (costExchangeRate) {
            existingAgg.costRateWeightedSum += costExchangeRate * qty;
            existingAgg.costUSDQtySum += qty;
          }
        }
        if (saleUSD) {
          existingAgg.totalSaleUSD += saleUSD * qty;
          if (saleExchangeRate) {
            existingAgg.saleRateWeightedSum += saleExchangeRate * qty;
            existingAgg.saleUSDQtySum += qty;
          }
        }
        if (fxGainTL) {
          existingAgg.totalFxGainTL += fxGainTL;
        }
      }

      partAggMap.set(aggKey, existingAgg);
    });
  });

  const grandProfit = grandTotalSale - grandTotalCost;
  const grandMargin = grandTotalCost > 0 ? Math.round((grandProfit / grandTotalCost) * 100) : 0;
  const avgCostRate = grandCostUSDQty > 0 ? Math.round((grandCostRateSum / grandCostUSDQty) * 100) / 100 : undefined;
  const avgSaleRate = grandSaleUSDQty > 0 ? Math.round((grandSaleRateSum / grandSaleUSDQty) * 100) / 100 : undefined;
  const grandRateDiff = avgCostRate && avgSaleRate ? Math.round((avgSaleRate - avgCostRate) * 100) / 100 : undefined;

  // Sheet 1: Summary
  const summaryData = [
    { 'Metrik': 'Firma Adı', 'Değer': companyInfo.name || 'Teknik Servis' },
    { 'Metrik': 'Rapor Dönemi', 'Değer': formattedPeriodName },
    { 'Metrik': 'Rapor Tarihi', 'Değer': new Date().toLocaleDateString('tr-TR') },
    { 'Metrik': 'İncelenen Servis Sayısı', 'Değer': filteredTickets.length },
    { 'Metrik': 'Toplam Düşülen / Kullanılan Parça Adedi (Birim)', 'Değer': grandTotalQty },
    { 'Metrik': 'Kullanılan Farklı Parça Çeşidi', 'Değer': partAggMap.size },
    { 'Metrik': 'Toplam Parça Alış / Maliyet Tutarı (TL)', 'Değer': grandTotalCost },
    { 'Metrik': 'Toplam Parça Satış Tutarı (TL)', 'Değer': grandTotalSale },
    { 'Metrik': 'Parça Satışından Elde Edilen Net Kâr (TL)', 'Değer': grandProfit },
    { 'Metrik': 'Genel Parça Kâr Marjı (%)', 'Değer': `%${grandMargin}` },
    { 'Metrik': '----------------------------------', 'Değer': '----------------' },
    { 'Metrik': '--- DÖVİZ & KUR FARKI METRİKLERİ ---', 'Değer': '----------------' },
    { 'Metrik': 'Toplam Dolar Alış Hacmi ($)', 'Değer': `$${grandTotalCostUSD.toFixed(2)}` },
    { 'Metrik': 'Toplam Dolar Satış Hacmi ($)', 'Değer': `$${grandTotalSaleUSD.toFixed(2)}` },
    { 'Metrik': 'Ağırlıklı Ortalama Alış Dolar Kuru (USD/TL)', 'Değer': avgCostRate ? `${avgCostRate.toFixed(2)} TL` : '-' },
    { 'Metrik': 'Ağırlıklı Ortalama Satış Dolar Kuru (USD/TL)', 'Değer': avgSaleRate ? `${avgSaleRate.toFixed(2)} TL` : '-' },
    { 'Metrik': 'Ortalama Kur Değişimi Farkı (₺/USD)', 'Değer': grandRateDiff !== undefined ? `${grandRateDiff >= 0 ? '+' : ''}${grandRateDiff.toFixed(2)} TL/USD` : '-' },
    { 'Metrik': 'Kur Farkından Doğan Toplam TL Kazancı (TL)', 'Değer': grandTotalFxGainTL },
    { 'Metrik': 'Güncel TCMB Dolar Kuru (USD/TL)', 'Değer': `${liveRate.toFixed(4)} TL` },
  ];

  // Sheet 2: Consolidated by Part
  const consolidatedData = Array.from(partAggMap.values())
    .sort((a, b) => b.totalQty - a.totalQty)
    .map(p => {
      const netK = p.totalSale - p.totalCost;
      const mPerc = p.totalCost > 0 ? Math.round((netK / p.totalCost) * 100) : 0;
      const pAvgCostRate = p.costUSDQtySum > 0 ? Math.round((p.costRateWeightedSum / p.costUSDQtySum) * 100) / 100 : undefined;
      const pAvgSaleRate = p.saleUSDQtySum > 0 ? Math.round((p.saleRateWeightedSum / p.saleUSDQtySum) * 100) / 100 : undefined;
      const pRateDiff = pAvgCostRate && pAvgSaleRate ? Math.round((pAvgSaleRate - pAvgCostRate) * 100) / 100 : undefined;

      return {
        'Parça / Ürün Adı': p.partName,
        'Stok Kodu': p.partCode,
        'Kategori': p.category,
        'Para Birimi': p.isUSD ? 'USD ($)' : 'TL (₺)',
        'Toplam Kullanılan Adet': p.totalQty,
        'Kullanıldığı Servis Sayısı': p.timesUsed,
        'Ort. Alış Dolar Kuru (USD/TL)': pAvgCostRate ? `${pAvgCostRate.toFixed(2)} TL` : '-',
        'Ort. Satış Dolar Kuru (USD/TL)': pAvgSaleRate ? `${pAvgSaleRate.toFixed(2)} TL` : '-',
        'Kur Değişimi Farkı (₺/USD)': pRateDiff !== undefined ? `${pRateDiff >= 0 ? '+' : ''}${pRateDiff.toFixed(2)} TL` : '-',
        'Kur Farkı TL Geliri': p.totalFxGainTL || 0,
        'Toplam Alış Maliyeti (TL)': p.totalCost,
        'Toplam Satış Tutarı (TL)': p.totalSale,
        'Net Kâr (TL)': netK,
        'Kâr Marjı (%)': `%${mPerc}`,
        'Depodaki Güncel Stok': p.currentStock,
      };
    });

  // Sheet 3: Detailed Ticket Part Usages
  const detailData = detailedRows.map(r => ({
    'Tarih': r.date,
    'Servis No': r.ticketId,
    'Müşteri Adı': r.customerName,
    'Telefon': r.customerPhone,
    'Cihaz & Model': r.device,
    'Parça Adı': r.partName,
    'Stok Kodu': r.partCode,
    'Kategori': r.category,
    'Para Birimi': r.isUSD ? 'USD' : 'TL',
    'Kullanılan Miktar': r.quantity,
    'Birim Alış (TL)': r.unitCost,
    'Birim Alış ($)': r.costPriceUSD ? `$${r.costPriceUSD.toFixed(2)}` : '-',
    'Alış Dolar Kuru (USD/TL)': r.costExchangeRate ? `${r.costExchangeRate.toFixed(2)} TL` : '-',
    'Birim Satış (TL)': r.unitPrice,
    'Birim Satış ($)': r.salePriceUSD ? `$${r.salePriceUSD.toFixed(2)}` : '-',
    'Satış Dolar Kuru (USD/TL)': r.saleExchangeRate ? `${r.saleExchangeRate.toFixed(2)} TL` : '-',
    'Kur Değişimi (₺/USD)': r.rateDifference !== undefined ? `${r.rateDifference >= 0 ? '+' : ''}${r.rateDifference.toFixed(2)} TL` : '-',
    'Kur Farkı TL Kazancı': r.fxGainTL || 0,
    'Toplam Maliyet (TL)': r.totalCost,
    'Toplam Satış (TL)': r.totalSale,
    'Net Kâr (TL)': r.netProfit,
    'Kâr Oranı (%)': `%${r.marginPercent}`,
    'Depoda Kalan': r.currentStock,
    'Kaynak': r.isStocked ? 'Depo / Stoklu' : 'Harici Parça',
  }));

  const wsSummary = XLSX.utils.json_to_sheet(summaryData);
  const wsConsolidated = XLSX.utils.json_to_sheet(consolidatedData.length > 0 ? consolidatedData : [{ 'Bilgi': 'Bu dönemde kullanılan parça bulunamadı' }]);
  const wsDetail = XLSX.utils.json_to_sheet(detailData.length > 0 ? detailData : [{ 'Bilgi': 'Bu dönemde kullanılan parça bulunamadı' }]);

  const autoFitCols = (ws: XLSX.WorkSheet, rows: any[]) => {
    if (!rows || rows.length === 0) return;
    const colWidths = Object.keys(rows[0]).map(key => {
      let maxLen = key.toString().length;
      rows.forEach(r => {
        const val = r[key];
        if (val !== null && val !== undefined) {
          maxLen = Math.max(maxLen, val.toString().length);
        }
      });
      return { wch: Math.min(Math.max(maxLen + 3, 12), 40) };
    });
    ws['!cols'] = colWidths;
  };

  autoFitCols(wsSummary, summaryData);
  if (consolidatedData.length > 0) autoFitCols(wsConsolidated, consolidatedData);
  if (detailData.length > 0) autoFitCols(wsDetail, detailData);

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Stok Tüketim Özeti');
  XLSX.utils.book_append_sheet(wb, wsConsolidated, 'Parça Bazında Kümülatif');
  XLSX.utils.book_append_sheet(wb, wsDetail, 'Detaylı Servis Dökümü');

  const safeKey = periodKey.replace(/[^a-zA-Z0-9_-]/g, '_');
  XLSX.writeFile(wb, `Stok_Dusum_Raporu_${safeKey}.xlsx`);
}

export function exportTechnicianReportExcel(
  technicianName: string,
  periodTitle: string,
  tickets: ServiceTicket[],
  companyInfo: CompanyInfo,
  periodKey = 'all'
) {
  const wb = XLSX.utils.book_new();

  let totalRevenue = 0;
  let totalLaborRevenue = 0;
  let totalPartsRevenue = 0;
  let totalPartsCost = 0;
  let totalSubServiceCost = 0;
  let totalCollected = 0;
  let totalRemaining = 0;

  let totalCash = 0;
  let totalCard = 0;
  let totalTransfer = 0;

  let deliveredCount = 0;
  let activeCount = 0;
  let returnedCount = 0;

  const ticketRows = tickets.map(t => {
    const { subtotal, vatAmount, partsTotal, grandTotal } = calculateTicketTotal(
      t.parts,
      t.laborCost,
      t.vatRate,
      t.discount,
      t.isVatIncluded, t.roundingAdjustment || 0
    );
    const partsCost = calculateTicketPartsCost(t.parts);
    const subCost = t.subServiceCost || 0;
    const split = periodKey === 'all' ? getTicketPaymentSplit(t) : getTicketPaymentSplitForPeriod(t, periodKey);
    const paid = split.total;
    const ratio = grandTotal > 0 ? paid / grandTotal : 0;
    const reportDate = getTicketReportDate(t);
    const serviceInPeriod = periodKey === 'all' || Boolean(reportDate && reportDate.startsWith(periodKey));

    totalRevenue += paid;
    totalLaborRevenue += (t.laborCost || 0) * ratio;
    totalPartsRevenue += (partsTotal || 0) * ratio;
    if (serviceInPeriod) {
      totalPartsCost += partsCost;
      totalSubServiceCost += subCost;
    }

    if (t.status === 'TeslimEdildi') deliveredCount++;
    else if (t.status === 'IadeEdildi' || t.status === 'IadeEdilecek') returnedCount++;
    else activeCount++;

    const remaining = Math.max(0, grandTotal - getTicketPaymentSplit(t).total);
    totalCollected += paid;
    totalRemaining += remaining;

    totalCash += split.cash;
    totalCard += split.card;
    totalTransfer += split.transfer;

    const tDate = (getTicketReportDate(t) || '').substring(0, 10);
    const deviceName = [t.deviceType, t.deviceBrand, t.deviceModel].filter(Boolean).join(' ');

    return {
      'Tarih': tDate,
      'Servis No': t.id,
      'Müşteri Adı': t.customerName || '',
      'Telefon': t.customerPhone || '',
      'Cihaz': deviceName,
      'Durum': STATUS_LABELS[t.status] || t.status,
      'İşçilik Ücreti (TL)': t.laborCost || 0,
      'Parça Tutarı (TL)': partsTotal || 0,
      'Parça Maliyeti (TL)': partsCost,
      'Toplam Tutar (TL)': grandTotal,
      'Tahsil Edilen (TL)': paid,
      'Kalan Bakiye (TL)': remaining,
      'Nakit (TL)': split.cash,
      'Kredi Kartı (TL)': split.card,
      'EFT/Havale (TL)': split.transfer,
      'Ödeme Yöntemi': formatTicketPaymentMethodDisplay(t),
      'Ödeme Durumu': t.paymentStatus === 'Odendi' ? 'Ödendi' : t.paymentStatus === 'KismiOdendi' ? 'Kısmi Ödendi' : 'Bekliyor',
    };
  });

  // Summary Sheet
  const summaryData = [
    { 'Metrik': 'Firma Adı', 'Değer': companyInfo.name },
    { 'Metrik': 'İlgili Teknisyen', 'Değer': technicianName },
    { 'Metrik': 'Rapor Dönemi', 'Değer': periodTitle },
    { 'Metrik': 'Rapor Oluşturma Tarihi', 'Değer': new Date().toLocaleDateString('tr-TR') },
    { 'Metrik': 'Toplam Baktığı Cihaz / Servis', 'Değer': `${tickets.length} Adet` },
    { 'Metrik': 'Teslim Edilen Cihaz', 'Değer': `${deliveredCount} Adet` },
    { 'Metrik': 'İşlemde / Aktif Cihaz', 'Değer': `${activeCount} Adet` },
    { 'Metrik': 'İade Edilen Cihaz', 'Değer': `${returnedCount} Adet` },
    { 'Metrik': 'Toplam İşçilik Geliri (TL)', 'Değer': totalLaborRevenue },
    { 'Metrik': 'Toplam Yedek Parça Satışı (TL)', 'Değer': totalPartsRevenue },
    { 'Metrik': 'Toplam Servis Cirosu (TL)', 'Değer': totalRevenue },
    { 'Metrik': 'Kasa Girişi: Nakit (TL)', 'Değer': totalCash },
    { 'Metrik': 'Kasa Girişi: Kredi Kartı (TL)', 'Değer': totalCard },
    { 'Metrik': 'Kasa Girişi: EFT / Havale (TL)', 'Değer': totalTransfer },
    { 'Metrik': 'Toplam Tahsil Edilen (TL)', 'Değer': totalCollected },
    { 'Metrik': 'Bekleyen Alacak / Kalan Bakiye (TL)', 'Değer': totalRemaining },
  ];

  const wsSummary = XLSX.utils.json_to_sheet(summaryData);
  const wsDetails = XLSX.utils.json_to_sheet(ticketRows.length > 0 ? ticketRows : [{ 'Bilgi': 'Bu dönemde teknisyene ait servis kaydı bulunamadı' }]);
  const collectionRows = getPeriodCollectionRows(tickets, periodKey);
  const wsCollections = XLSX.utils.json_to_sheet(collectionRows.length ? collectionRows : [{ 'Ödeme Tarihi': '', 'Servis Formu': '', 'Müşteri': 'Bu dönemde tahsilat kaydı yok', 'Cihaz': '', 'Ödeme Türü': '', 'Alınan Tutar (TL)': 0 }]);

  const autoFitCols = (ws: XLSX.WorkSheet, rows: any[]) => {
    if (!rows || rows.length === 0) return;
    const colWidths = Object.keys(rows[0]).map(key => {
      let maxLen = key.toString().length;
      rows.forEach(r => {
        const val = r[key];
        if (val !== null && val !== undefined) {
          maxLen = Math.max(maxLen, val.toString().length);
        }
      });
      return { wch: Math.min(Math.max(maxLen + 3, 12), 40) };
    });
    ws['!cols'] = colWidths;
  };

  autoFitCols(wsSummary, summaryData);
  if (ticketRows.length > 0) autoFitCols(wsDetails, ticketRows);

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Teknisyen Performans Özeti');
  XLSX.utils.book_append_sheet(wb, wsDetails, 'Cihaz & İşlem Listesi');
  XLSX.utils.book_append_sheet(wb, wsCollections, 'Tahsilat Hareketleri');

  const safeTech = technicianName.replace(/[^a-zA-Z0-9_-]/g, '_');
  const safePeriod = periodTitle.replace(/[^a-zA-Z0-9_-]/g, '_');
  XLSX.writeFile(wb, `Teknisyen_Raporu_${safeTech}_${safePeriod}.xlsx`);
}


